// 📁 WatermarkRow.tsx (with isEditing-safe display fallback)

import React, { useEffect, useRef, useCallback, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Select, MenuItem } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteBtn from './DeleteBtn';
import { updateWatermark, setWatermarkList } from '@modules/editor';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { HexColorPicker } from "react-colorful";
import { toast, Flip } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface WatermarkRowProps {
    idx: number;
}

const options = [
    { value: 'image', label: '이미지' },
    { value: 'text', label: '텍스트' }
];

const WatermarkRow = memo(({ idx }: WatermarkRowProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const textInputRef = useRef<HTMLDivElement>(null);
    const xRef = useRef<HTMLDivElement>(null);
    const yRef = useRef<HTMLDivElement>(null);
    const fontSizeRef = useRef<HTMLDivElement>(null);
    const widthRef = useRef<HTMLDivElement>(null);
    const heightRef = useRef<HTMLDivElement>(null);
    const watermarkList = useSelector((state: any) => state.editor.watermarkList);
    const canvasSize = useSelector((state: any) => state.editor.canvasSize);
    const el = watermarkList[idx];
    const isEditing = el?.isEditing || false;

    // el이 undefined인 경우 기본값 반환
    if (!el) {
        return null;
    }
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [draft, setDraft] = useState({
        text: el?.text || '',
        x: el?.x?.toString() || '0',
        y: el?.y?.toString() || '0',
        fontSize: el?.fontSize?.toString() || '16',
        width: el?.width?.toString() || '100',
        height: el?.height?.toString() || '100'
    });
    const [errorFields, setErrorFields] = useState<{ [key: string]: boolean }>({});

    const isValidNumberString = (value: string) => /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;

    // 유효성 검사 함수들
    const validateX = (x: number, width: number) => {
        return x >= 0 && x + width <= canvasSize.canvasWidth;
    };

    const validateY = (y: number, height: number) => {
        return y >= 0 && y + height <= canvasSize.canvasHeight;
    };

    const validateWidth = (x: number, width: number) => {
        return width > 0 && x + width <= canvasSize.canvasWidth;
    };

    const validateHeight = (y: number, height: number) => {
        return height > 0 && y + height <= canvasSize.canvasHeight;
    };

    // 숫자 형식 검사만 하는 함수 (UI 표시용)
    const getNumberFormatErrors = (fields: typeof draft) => ({
        x: !isValidNumberString(fields.x),
        y: !isValidNumberString(fields.y),
        fontSize: !isValidNumberString(fields.fontSize),
        width: !isValidNumberString(fields.width),
        height: !isValidNumberString(fields.height)
    });

    // 전체 유효성 검사 함수 (저장 시 검사용)
    const getValidationErrors = (fields: typeof draft) => ({
        x: !isValidNumberString(fields.x) || !validateX(Number(fields.x), Number(fields.width)),
        y: !isValidNumberString(fields.y) || !validateY(Number(fields.y), Number(fields.height)),
        fontSize: !isValidNumberString(fields.fontSize),
        width: !isValidNumberString(fields.width) || !validateWidth(Number(fields.x), Number(fields.width)),
        height: !isValidNumberString(fields.height) || !validateHeight(Number(fields.y), Number(fields.height))
    });

    useEffect(() => {
        if (isEditing && el) {
            setDraft({
                text: el.text || '',
                x: el.x?.toString() || '0',
                y: el.y?.toString() || '0',
                fontSize: el.fontSize?.toString() || '16',
                width: el.width?.toString() || '100',
                height: el.height?.toString() || '100'
            });
            if (textInputRef.current) textInputRef.current.textContent = el.text || '';
            if (xRef.current) xRef.current.textContent = el.x.toString();
            if (yRef.current) yRef.current.textContent = el.y.toString();
            if (fontSizeRef.current) fontSizeRef.current.textContent = el.fontSize.toString();
            if (widthRef.current) widthRef.current.textContent = el.width.toString();
            if (heightRef.current) heightRef.current.textContent = el.height.toString();
        }
    }, [isEditing, el]);

    const updateWatermarkProperty = useCallback((key: string, value: string) => {
        if (!el) return;
        if (key === 'color') {
            dispatch(updateWatermark({ ...el, color: value, index: idx }));
        } else if (isValidNumberString(value)) {
            dispatch(updateWatermark({ ...el, [key]: Number(value), index: idx }));
        }
    }, [dispatch, el, idx]);

    const toggleEdit = () => {
        if (el.isEditing) {
            const errors = getValidationErrors(draft);
            if (Object.values(errors).some(Boolean)) {
                let errorMessage = t('vod.manage.editor_tab.enter_value_correctly');

                if (errors.x) {
                    errorMessage = t('vod.manage.editor_tab.x_coordinate_error', { max: Math.floor(canvasSize.canvasWidth - Number(draft.width)) });
                } else if (errors.y) {
                    errorMessage = t('vod.manage.editor_tab.y_coordinate_error', { max: Math.floor(canvasSize.canvasHeight - Number(draft.height)) });
                } else if (errors.width) {
                    errorMessage = t('vod.manage.editor_tab.width_error', { max: Math.floor(canvasSize.canvasWidth - Number(draft.x)) });
                } else if (errors.height) {
                    errorMessage = t('vod.manage.editor_tab.height_error', { max: Math.floor(canvasSize.canvasHeight - Number(draft.y)) });
                } else if (errors.fontSize) {
                    errorMessage = t('vod.manage.editor_tab.font_size_error');
                }

                toast.error(errorMessage, {
                    position: "top-center",
                    transition: Flip
                });
                return;
            }
            dispatch(updateWatermark({
                ...el,
                x: Number(draft.x),
                y: Number(draft.y),
                fontSize: Number(draft.fontSize),
                width: Number(draft.width),
                height: Number(draft.height),
                text: draft.text,
                index: idx,
                isEditing: false
            }));
        } else {
            dispatch(updateWatermark({
                ...el,
                isEditing: true,
                index: idx
            }));
        }
    };

    return (
        <Box className="watermark-row">
            <Box className="watermark-label">
                <Typography>{t('vod.manage.editor_tab.watermark')} {idx + 1}</Typography>
            </Box>
            <Box className="watermark-type">
                <Select
                    className="watermark-select"
                    id={`watermark-type-select-${idx}`}
                    name={`watermark-type-select-${idx}`}
                    fullWidth
                    value={el.type}
                    onChange={(e) => {
                        dispatch(updateWatermark({ ...el, type: e.target.value, index: idx }));
                    }}
                >
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <Box className="watermark-content">
                {el.type === 'text' ? (
                    <div className="watermark-text-container">
                        {isEditing ? (
                            <div
                                ref={textInputRef}
                                className="watermark editable-text is-editable"
                                contentEditable
                                suppressContentEditableWarning
                                onInput={(e) => {
                                    const value = e?.currentTarget?.textContent;
                                    if (value != null) setDraft(prev => ({ ...prev, text: value }));
                                }}
                                onBlur={() => updateWatermarkProperty('text', draft.text)}
                            />
                        ) : (
                            <div className="watermark">{el.text}</div>
                        )}
                        {isEditing && (
                            <div className="color-picker-container">
                                <div
                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                    className="color-picker-button"
                                    style={{ backgroundColor: el.color || '#000000' }}
                                />
                                {showColorPicker && (
                                    <div className="color-picker-dropdown">
                                        <div
                                            className="color-picker-overlay"
                                            onClick={() => setShowColorPicker(false)}
                                        />
                                        <div className="color-picker-content">
                                            <HexColorPicker
                                                color={el.color || '#000000'}
                                                onChange={(color) => updateWatermarkProperty('color', color)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : null}
            </Box>
            <Box className="watermark-position-size">
                <Box className="watermark-position">
                    <Typography>{t('vod.manage.editor_tab.position')}</Typography>
                    <Box className="watermark-coordinates">
                        <Box className="watermark-coordinate">
                            <Typography>X</Typography>
                            {isEditing ? (
                                <div
                                    ref={xRef}
                                    className={`watermark editable-text is-editable ${errorFields.x ? 'error-field' : ''}`}
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, x: value }));
                                        setErrorFields(prev => ({ ...prev, x: !isValidNumberString(value) }));
                                    }}
                                />
                            ) : (
                                <div className="watermark">{el.x}</div>
                            )}
                        </Box>
                        <Box className="watermark-coordinate">
                            <Typography>Y</Typography>
                            {isEditing ? (
                                <div
                                    ref={yRef}
                                    className={`watermark editable-text is-editable ${errorFields.y ? 'error-field' : ''}`}
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, y: value }));
                                        setErrorFields(prev => ({ ...prev, y: !isValidNumberString(value) }));
                                    }}
                                />
                            ) : (
                                <div className="watermark">{el.y}</div>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box className="watermark-size">
                    <Typography>{t('vod.manage.editor_tab.size')}</Typography>
                    <Box className="watermark-dimensions">
                        {el.type === 'text' ? (
                            <Box className="watermark-dimension">
                                {isEditing ? (
                                    <div
                                        ref={fontSizeRef}
                                        className={`watermark editable-text is-editable ${errorFields.fontSize ? 'error-field' : ''}`}
                                        contentEditable
                                        suppressContentEditableWarning
                                        onInput={(e) => {
                                            const value = e?.currentTarget?.textContent ?? "";
                                            setDraft(prev => ({ ...prev, fontSize: value }));
                                            setErrorFields(prev => ({ ...prev, fontSize: !isValidNumberString(value) }));
                                        }}
                                    />
                                ) : (
                                    <div className="watermark">{el.fontSize}</div>
                                )}
                            </Box>
                        ) : (
                            <>
                                <Box className="watermark-dimension">
                                    <Typography>{t('vod.manage.editor_tab.width')}</Typography>
                                    {isEditing ? (
                                        <div
                                            ref={widthRef}
                                            className={`watermark editable-text is-editable ${errorFields.width ? 'error-field' : ''}`}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={(e) => {
                                                const value = e?.currentTarget?.textContent ?? "";
                                                setDraft(prev => ({ ...prev, width: value }));
                                                setErrorFields(prev => ({ ...prev, width: !isValidNumberString(value) }));
                                            }}
                                        />
                                    ) : (
                                        <div className="watermark">{el.width}</div>
                                    )}
                                </Box>
                                <Box className="watermark-dimension">
                                    <Typography>{t('vod.manage.editor_tab.height')}</Typography>
                                    {isEditing ? (
                                        <div
                                            ref={heightRef}
                                            className={`watermark editable-text is-editable ${errorFields.height ? 'error-field' : ''}`}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={(e) => {
                                                const value = e?.currentTarget?.textContent ?? "";
                                                setDraft(prev => ({ ...prev, height: value }));
                                                setErrorFields(prev => ({ ...prev, height: !isValidNumberString(value) }));
                                            }}
                                        />
                                    ) : (
                                        <div className="watermark">{el.height}</div>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box className="watermark-btns">
                {isEditing ? (
                    <CheckIcon onClick={toggleEdit} className="edit-button check-button" />
                ) : (
                    <EditIcon onClick={toggleEdit} className="edit-button edit-icon" />
                )}
                <DeleteBtn idx={idx} />
            </Box>
        </Box>
    );
});

export default WatermarkRow;